import { Fab, Stack, Typography } from '@mui/material'
import { Icons } from 'utoniq-ui'

import { ConsoleDmRoomsQuery, Maybe } from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { DmRoomBox } from '@/components/organisms/dmRoom/DmRoomBox'
import { DmRoomSkeleton } from '@/components/organisms/dmRoom/DmRoomSkeleton'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  dmRooms: Maybe<ConsoleDmRoomsQuery['artistDmRooms']>
  loading: boolean
  hasNextPage: boolean
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ConsoleDmRoomsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('dmRooms')
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleDmRooms} />

      <ConsoleBaseLayout
        header={{
          left: <ArtistHeaderItemsLeft />,
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Stack sx={{ py: 3, px: 5 }} spacing={2}>
          <Typography variant="h3">{t('title')}</Typography>
          <Typography variant="body1">{t('description')}</Typography>
        </Stack>
        <Stack sx={{ px: 5 }}>
          <ScrollFetcher
            componentProps={{ sx: { pt: 2, pb: 5 }, spacing: 5 }}
            nodes={state.dmRooms?.edges.map(({ node }) => (
              <DmRoomBox key={node.id} dmRoom={node} isConsole={true} />
            ))}
            onFetchMore={onFetchMore}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            emptyMessage={t('emptyDmRoom')}
            loadingComponent={
              <Stack spacing={5} sx={{ pb: 7 }}>
                {Array.from(Array(4).keys()).map(key => (
                  <DmRoomSkeleton key={key} />
                ))}
              </Stack>
            }
          />
        </Stack>
        <BaseCell />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const BaseCell: Cell = () => {
  const { router, routerPages } = useRouter()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={() => router.push(routerPages.ConsoleDmRoomCreate)}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
    </Stack>
  )
}
